import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  fetchPrescription,
  updatePrescription,
  fetchRefills,
  addRefill,
  fetchAlerts,
  clearPrescription,
} from '../store/prescriptionsSlice'
import type { PrescriptionDetail as PrescriptionDetailType } from '../store/prescriptionsSlice'
import { PrescriptionDetailHeader } from '../components/prescriptions/PrescriptionDetailHeader'
import { MedicationInfo } from '../components/prescriptions/MedicationInfo'
import { DosageCalculator } from '../components/prescriptions/DosageCalculator'
import { RefillHistory } from '../components/prescriptions/RefillHistory'
import { PrescriptionAlerts } from '../components/prescriptions/PrescriptionAlerts'

export function PrescriptionDetailPage() {
  const { prescriptionId } = useParams<{ prescriptionId: string }>()
  const dispatch = useAppDispatch()
  const { prescription, refills, alerts, loading } = useAppSelector((s) => s.prescriptions)

  useEffect(() => {
    if (prescriptionId) {
      dispatch(fetchPrescription(prescriptionId))
      dispatch(fetchRefills(prescriptionId))
      dispatch(fetchAlerts(prescriptionId))
    }
    return () => {
      dispatch(clearPrescription())
    }
  }, [dispatch, prescriptionId])

  const handleUpdate = async (data: Partial<PrescriptionDetailType>) => {
    if (!prescriptionId) return
    await dispatch(updatePrescription({ id: prescriptionId, data }))
  }

  const handleRefill = async (data: { quantity_dispensed: number; dispensed_by: string; notes: string }) => {
    if (!prescriptionId || !prescription) return
    await dispatch(addRefill({
      prescription_id: prescriptionId,
      ...data,
    }))

    // If controlled substance, auto-create log entry
    if (prescription.medication_controlled) {
      try {
        await fetch('/.netlify/functions/controlled-substances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            medication_id: prescription.medication_id,
            patient_id: prescription.patient_id,
            prescription_id: prescriptionId,
            transaction_type: 'Dispensed',
            quantity: data.quantity_dispensed,
            performed_by: data.dispensed_by,
            notes: data.notes || `Refill for RX-${String(prescription.prescription_number).padStart(4, '0')}`,
          }),
        })
      } catch {
        // Non-critical: log entry creation failure shouldn't block refill
      }
    }

    // Re-fetch prescription to get updated remaining_refills from server
    dispatch(fetchPrescription(prescriptionId))
  }

  const handleApplyDosage = async (data: { dosage_calculated: number; dose_rate: number }) => {
    if (!prescriptionId) return
    await dispatch(updatePrescription({
      id: prescriptionId,
      data: {
        dosage: `${data.dosage_calculated} mg`,
        dosage_calculated: data.dosage_calculated,
        dose_rate: data.dose_rate,
      },
    }))
  }

  if (loading || !prescription) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="prescription-detail-page">
        <p className="text-[13px] text-text-muted">Loading prescription...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="prescription-detail-page">
      <PrescriptionDetailHeader
        prescription={prescription}
        onUpdate={handleUpdate}
        onRefill={handleRefill}
      />

      <div className="flex flex-col gap-4 mt-4">
        <MedicationInfo prescription={prescription} />
        <DosageCalculator prescription={prescription} onApply={handleApplyDosage} />
        <RefillHistory
          refills={refills}
          totalRefills={prescription.total_refills}
          remainingRefills={prescription.remaining_refills}
        />
        <PrescriptionAlerts alerts={alerts} />
      </div>
    </div>
  )
}
